import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Globe, Phone } from 'lucide-react';

export const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const languages = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'bg', label: 'Български', flag: '🇧🇬' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  ];

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/fleet', label: t('fleet') },
    { path: '/about', label: t('about') },
    { path: '/contact', label: t('contact') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="header">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt="Universal Rent a Car and Car Wash" 
              className="w-14 h-14 rounded-full object-cover shadow-lg group-hover:scale-105 transition-transform"
            />
            <div className="hidden sm:block">
              <span className="font-serif text-2xl font-bold tracking-tight">
                <span className="text-foreground">Univer</span><span className="text-gold">sal</span>
              </span>
              <span className="block text-[10px] text-muted-foreground tracking-[0.3em] uppercase font-medium">Rent a Car</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive(link.path) ? 'text-accent' : 'text-foreground'
                }`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Phone */}
            <a 
              href="tel:+306972252180" 
              className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>+30 697 2252180</span>
            </a>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{language.toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-accent/10' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book Now Button */}
            <Link to="/fleet">
              <Button className="hidden sm:flex bg-accent hover:bg-accent/90 text-white" data-testid="book-now-btn">
                {t('bookNow')}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden border-2 border-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    isActive(link.path)
                      ? 'bg-accent/10 text-accent'
                      : 'text-foreground hover:bg-secondary'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/admin"
                className="px-4 py-3 text-sm font-medium text-muted-foreground hover:bg-secondary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('admin')}
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-primary text-primary-foreground py-16" data-testid="footer">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img 
                src="/logo.png" 
                alt="Universal Rent a Car and Car Wash" 
                className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
              />
              <div>
                <span className="font-serif text-3xl font-bold tracking-tight">
                  <span className="text-white">Univer</span><span className="text-gold">sal</span>
                </span>
                <span className="block text-[11px] text-white/50 tracking-[0.35em] uppercase font-medium">Rent a Car</span>
              </div>
            </div>
            <p className="text-white/70 max-w-md">
              {t('aboutDescription')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-white/70 hover:text-white transition-colors">{t('home')}</Link></li>
              <li><Link to="/fleet" className="text-white/70 hover:text-white transition-colors">{t('fleet')}</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-white transition-colors">{t('about')}</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-white transition-colors">{t('contact')}</Link></li>
              <li><Link to="/admin" className="text-white/70 hover:text-gold transition-colors">{t('admin')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">{t('contact')}</h4>
            <ul className="space-y-2 text-white/70">
              <li>{t('address')}</li>
              <li>
                <a href="tel:+306972252180" className="hover:text-white transition-colors">
                  +30 697 2252180
                </a>
              </li>
              <li>
                <a href="tel:+306970095042" className="hover:text-white transition-colors">
                  +30 6970095042
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/50 text-sm">
          © {new Date().getFullYear()} Universal Rent a Car. {t('rights')}.
        </div>
      </div>
    </footer>
  );
};
